import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { App } from '@/types/App';
import { closeSKOverlay } from '@/utils/nativeInterface';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useAvailableAppsContext } from '@/contexts/AvailableAppsContext';
import { toast } from '@/hooks/use-toast';
import { api } from '../../api';

interface AppDetailHeaderProps {
  app: App;
  installPressed?: boolean;
  onInstallPress?: () => void;
  onBack?: () => void;
}

const AppDetailHeader: React.FC<AppDetailHeaderProps> = ({ app, installPressed = false, onInstallPress, onBack }) => {
  const navigate = useNavigate();
  const { serial, source } = useEffectiveSerial();
  const { whitelistedApps, categoryApps } = useAvailableAppsContext();
  const installApp = useMutation(api.devices.apps.install);
  const [isInstalling, setIsInstalling] = useState(false);

  // Find the Convex document ID for this app based on trackId
  // The install API requires a Convex Id<"apps">, not the App Store trackId
  const convexAppId = useMemo(() => {
    const allLoadedApps = [...whitelistedApps, ...categoryApps];
    const foundApp = allLoadedApps.find(a => a.appStoreId === app.appStoreId);
    return foundApp?.id;
  }, [whitelistedApps, categoryApps, app.appStoreId]);

  // Debug logging - only in development
  if (import.meta.env.DEV) {
    console.log('AppDetailHeader - Serial Resolution:', { serial, source, appStoreId: app.appStoreId, convexAppId });
  }

  const handleBackNavigation = async () => {
    try {
      await closeSKOverlay();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error closing SKOverlay:', error);
      }
    }
    
    if (onBack) {
      onBack();
    } else {
      // Safe back navigation with fallback
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/', { replace: true });
      }
    }
  };

  const handleInstall = async () => {
    if (import.meta.env.DEV) {
      console.log('Install clicked:', { app: app.name, appStoreId: app.appStoreId, serial, convexAppId });
    }
    
    // Validate app store ID (trackId) is present
    if (!app.appStoreId || app.appStoreId.trim() === '') {
      console.error('No App Store ID (trackId) available');
      toast({
        title: "Installation Error",
        description: "App Store ID is missing. This app cannot be installed.",
        variant: "destructive"
      });
      return;
    }
    
    if (!serial) {
      console.error('No device serial available');
      toast({
        title: "Device Error",
        description: "Device serial is missing. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    // Check if we have the Convex document ID
    if (!convexAppId) {
      console.error('Convex document ID not found for app:', app.appStoreId);
      toast({
        title: "App Not Found",
        description: "This app is not in the allowed apps list. Please scroll down to load more apps and try again.",
        variant: "destructive"
      });
      return;
    }
    
    // Set loading state
    setIsInstalling(true);
    
    try {
      // Pass Convex document ID to the install API (not the trackId)
      console.log('Calling devices.apps.install API with convexAppId:', convexAppId);
      await installApp({
        app: convexAppId as any, // Convex document ID
        serial: serial
      });
      console.log('devices.apps.install API call completed successfully');
      
      // Call the onInstallPress callback
      if (onInstallPress) {
        console.log('Calling onInstallPress...');
        onInstallPress();
      }
      
    } catch (error) {
      console.error('Error during install process:', error);
      
      // Provide specific error messages based on error type
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      if (errorMessage.includes('not found') || errorMessage.includes('does not exist')) {
        toast({
          title: "App Not Available",
          description: "This app is not available in our database. Please try another app.",
          variant: "destructive"
        });
      } else if (errorMessage.includes('permission') || errorMessage.includes('denied')) {
        toast({
          title: "Permission Denied",
          description: "You don't have permission to install apps on this device.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Installation Failed",
          description: "Unable to install the app. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <div 
      className="bg-background/80 backdrop-blur-md flex-shrink-0 touch-none"
      style={{ touchAction: 'none' }}
    >
      <div className="p-4 pb-0">
        {/* Back Button */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={handleBackNavigation}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
        </div>
        
        {/* App Header */}
        <div className="flex items-center space-x-4 mb-4">
          <img
            src={app.icon}
            alt={`${app.name} icon`}
            className="w-16 h-16 rounded-xl object-cover bg-muted shadow-sm"
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-foreground mb-1">
                  {app.name}
                </h2>
                <p className="text-muted-foreground">
                  {app.category || 'Uncategorized'}
                </p>
              </div>
              <Button
                onClick={handleInstall}
                className={`h-8 px-6 text-sm font-semibold rounded-full transition-all active:scale-[0.97] ${
                  installPressed 
                    ? 'bg-muted text-muted-foreground' 
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                }`}
                disabled={(!app.appStoreId || app.appStoreId.trim() === '') || installPressed || isInstalling}
                aria-label={installPressed ? 'Continue installation below' : `Install ${app.name}`}
              >
                {isInstalling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" />
                    Installing...
                  </>
                ) : installPressed ? (
                  "Continue Below"
                ) : (!app.appStoreId || app.appStoreId.trim() === '') ? (
                  "Not Available"
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-1" aria-hidden="true" />
                    Install
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppDetailHeader;
